"use client";

import {
  GovernmentEmployerBasicRegistrationDto,
  GovernmentEmployerDocumentRegistrationDto,
  GovernmentEmployerLocationRegistrationDto,
  GovernmentEmployerPicRegistrationDto,
  RegisterGovernmentDto,
  registerGovernmentEmployer,
} from "@entities/employer";
import {
  GovernmentEmployerBasicRegistrationForm,
  GovernmentEmployerDocumentRegistrationForm,
  GovernmentEmployerLocationRegistrationForm,
  GovernmentEmployerPicRegistrationForm,
} from "@features/registration";
import { useClientConfig } from "@providers/app-provider/client-app-provider";
import { RadialProgress } from "@shared/ui";
import { Button, Checkbox } from "@ubud-ui/react-ui-kit";
import { useState, useTransition } from "react";
import { GovernmentEmployerBasicRegistrationPreview } from "./government-employer-basic-registration-preview";
import { GovernmentEmployerDocumentRegistrationPreview } from "./government-employer-document-registration-preview";
import { GovernmentEmployerLocationRegistrationPreview } from "./government-employer-location-registration-preview";
import { GovernmentEmployerPicRegistrationPreview } from "./government-employer-pic-registration-preview";
import { toast } from "sonner";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";

function RegistrationCardInfo() {
  return (
    <div className="mt-4 bg-gray-100 rounded-lg px-4 py-3 space-y-2">
      <div className="text-gray-500 text-sm font-normal">Instansi Anda</div>
      <div className="font-medium text-base gap-3 flex items-start">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="size-5 min-w-5 mt-0.5" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M19.375 16.2499C19.375 16.4157 19.3092 16.5746 19.1919 16.6918C19.0747 16.8091 18.9158 16.8749 18.75 16.8749H1.25C1.08424 16.8749 0.925268 16.8091 0.808058 16.6918C0.690848 16.5746 0.625 16.4157 0.625 16.2499C0.625 16.0841 0.690848 15.9252 0.808058 15.808C0.925268 15.6908 1.08424 15.6249 1.25 15.6249H18.75C18.9158 15.6249 19.0747 15.6908 19.1919 15.808C19.3092 15.9252 19.375 16.0841 19.375 16.2499ZM1.27344 7.67022C1.23626 7.53915 1.24294 7.39952 1.29244 7.27259C1.34194 7.14566 1.43156 7.03839 1.54766 6.96709L9.67266 1.96709C9.77111 1.90656 9.88442 1.87451 10 1.87451C10.1156 1.87451 10.2289 1.90656 10.3273 1.96709L18.4523 6.96709C18.5685 7.0383 18.6582 7.1455 18.7078 7.27238C18.7573 7.39925 18.7641 7.53886 18.7271 7.66994C18.69 7.80103 18.6111 7.91642 18.5024 7.99854C18.3938 8.08067 18.2612 8.12504 18.125 8.1249H16.25V13.1249H17.5C17.6658 13.1249 17.8247 13.1908 17.9419 13.308C18.0592 13.4252 18.125 13.5841 18.125 13.7499C18.125 13.9157 18.0592 14.0746 17.9419 14.1918C17.8247 14.3091 17.6658 14.3749 17.5 14.3749H2.5C2.33424 14.3749 2.17527 14.3091 2.05806 14.1918C1.94085 14.0746 1.875 13.9157 1.875 13.7499C1.875 13.5841 1.94085 13.4252 2.05806 13.308C2.17527 13.1908 2.33424 13.1249 2.5 13.1249H3.75V8.1249H1.875C1.7389 8.12495 1.6065 8.08057 1.49792 7.9985C1.38935 7.91643 1.31053 7.80117 1.27344 7.67022ZM11.25 12.4999C11.25 12.6657 11.3158 12.8246 11.4331 12.9418C11.5503 13.0591 11.7092 13.1249 11.875 13.1249C12.0408 13.1249 12.1997 13.0591 12.3169 12.9418C12.4342 12.8246 12.5 12.6657 12.5 12.4999V8.7499C12.5 8.58414 12.4342 8.42517 12.3169 8.30796C12.1997 8.19075 12.0408 8.1249 11.875 8.1249C11.7092 8.1249 11.5503 8.19075 11.4331 8.30796C11.3158 8.42517 11.25 8.58414 11.25 8.7499V12.4999ZM7.5 12.4999C7.5 12.6657 7.56585 12.8246 7.68306 12.9418C7.80027 13.0591 7.95924 13.1249 8.125 13.1249C8.29076 13.1249 8.44973 13.0591 8.56694 12.9418C8.68415 12.8246 8.75 12.6657 8.75 12.4999V8.7499C8.75 8.58414 8.68415 8.42517 8.56694 8.30796C8.44973 8.19075 8.29076 8.1249 8.125 8.1249C7.95924 8.1249 7.80027 8.19075 7.68306 8.30796C7.56585 8.42517 7.5 8.58414 7.5 8.7499V12.4999Z"
            fill="#6C727F"
          />
        </svg>
        Instansi Non Pemerintah Non-NIB
      </div>
    </div>
  );
}

export function GovernmentEmployerRegistration({ redirectUrl }: { redirectUrl?: string }) {
  const config = useClientConfig();
  const nacoUser = config?.nacoUser;

  const steps = ["Informasi umum", "Alamat entitas", "Admin pengelola (PIC)", "Dokumen pendukung"];

  const [step, setStep] = useState<number>(0);
  const [isShowPreview, setIsShowPreview] = useState<boolean>(false);

  const [basic, setBasic] = useState<GovernmentEmployerBasicRegistrationDto | null>(null);
  const [location, setLocation] = useState<GovernmentEmployerLocationRegistrationDto | null>(null);
  const [pic, setPic] = useState<GovernmentEmployerPicRegistrationDto | null>(null);
  const [document, setDocument] = useState<GovernmentEmployerDocumentRegistrationDto | null>(null);

  const [consents, setConsents] = useState([
    { id: "responsible", text: "Saya berwenang mendaftarkan instansi ini sebagai pemberi kerja di Karirhub", checked: false },
    { id: "data_correct", text: "Saya menyatakan data yang diisi adalah benar dan dapat dipertanggungjawabkan.", checked: false },
  ]);

  const handleSubmitBasic = (payload: GovernmentEmployerBasicRegistrationDto) => {
    setBasic(payload);
    setStep(1);
  };

  const handleSubmitLocation = (payload: GovernmentEmployerLocationRegistrationDto) => {
    setLocation(payload);
    setStep(2);
  };

  const handleSubmitPic = (payload: GovernmentEmployerPicRegistrationDto) => {
    setPic(payload);
    setStep(3);
  };

  const handleSubmitDocument = (payload: GovernmentEmployerDocumentRegistrationDto) => {
    setDocument(payload);
    setIsShowPreview(true);
  };

  const handleUpdateConsent = (id: string, check: boolean | "indeterminate") => {
    setConsents((prev) => {
      const newMap = prev.map((it) => {
        if (it.id === id) {
          return {
            ...it,
            checked: check === "indeterminate" ? false : check,
          };
        }

        return it;
      });

      return newMap;
    });
  };

  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isRegistering, startRegistering] = useTransition();

  const handleRegister = () => {
    setErrorMessage("");
    startRegistering(async () => {
      try {
        const response = await registerGovernmentEmployer({
          ...basic,
          ...location,
          ...pic,
          ...document,
        } as RegisterGovernmentDto);

        if (response && typeof response === "object" && "error" in response && response.error) {
          setErrorMessage(
            typeof response.errorDescription === "string"
              ? response.errorDescription
              : typeof response.error === "string"
                ? response.error
                : "Gagal mendaftar sebagai pemberi kerja.",
          );
        } else {
          toast.success("Pendaftaran Pemberi Kerja", {
            description: `🎉 Selamat! Pendaftaran sebagai pemberi kerja telah berhasil.`,
            duration: 3000,
          });
          setTimeout(() => {
            window.location.replace(redirectUrl || "/");
          }, 3000);
        }

        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : "Terjadi kesalahan saat mendaftar sebagai pemberi kerja.");
      }
    });
  };

  const getFormStep = () => {
    switch (step) {
      case 0:
      default:
        return (
          <GovernmentEmployerBasicRegistrationForm formValue={basic || undefined} onSubmit={handleSubmitBasic}>
            <div className="mt-4">
              <Button type="submit" className="w-full text-sm font-bold">
                Selanjutnya
              </Button>
            </div>
          </GovernmentEmployerBasicRegistrationForm>
        );

      case 1:
        return (
          <GovernmentEmployerLocationRegistrationForm formValue={location || undefined} onSubmit={handleSubmitLocation}>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <Button type="button" className="w-full text-sm font-bold" variant="outline" onClick={() => setStep(0)}>
                Kembali
              </Button>
              <Button type="submit" className="w-full text-sm font-bold">
                Selanjutnya
              </Button>
            </div>
          </GovernmentEmployerLocationRegistrationForm>
        );

      case 2:
        return (
          <GovernmentEmployerPicRegistrationForm
            onSubmit={handleSubmitPic}
            formValue={{
              pic_id_number: nacoUser?.username || "",
              pic_email: nacoUser?.email || "",
              pic_name: nacoUser?.name || "",
              pic_phone: nacoUser?.phoneNumber || "",
            }}
          >
            <div className="mt-6 grid grid-cols-2 gap-3">
              <Button type="button" className="w-full text-sm font-bold" variant="outline" onClick={() => setStep(1)}>
                Kembali
              </Button>
              <Button type="submit" className="w-full text-sm font-bold">
                Selanjutnya
              </Button>
            </div>
          </GovernmentEmployerPicRegistrationForm>
        );

      case 3:
        return (
          <GovernmentEmployerDocumentRegistrationForm formValue={document || undefined} onSubmit={handleSubmitDocument}>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <Button type="button" className="w-full text-sm font-bold" variant="outline" onClick={() => setStep(2)}>
                Kembali
              </Button>
              <Button type="submit" className="w-full text-sm font-bold">
                Daftar
              </Button>
            </div>
          </GovernmentEmployerDocumentRegistrationForm>
        );
    }
  };

  if (isShowPreview) {
    return (
      <>
        <h1 className="text-xl font-bold">Konfirmasi data pemberi kerja</h1>
        <p className="mt-2 text-sm font-normal text-gray-500">
          Verifikasi membutuhkan waktu minimal 3 hari kerja. Pantau status akun Anda secara berkala atau hubungi kami bila ada pertanyaan.
        </p>
        <RegistrationCardInfo />

        {errorMessage ? (
          <div className="mt-4 bg-red-100 rounded-lg px-4 py-3 space-y-3 text-red-600">
            <h2 className="font-semibold text-sm flex items-center gap-2">
              <ExclamationCircleIcon className="size-5 min-w-5" />
              <span>Terjadi kesalahan</span>
            </h2>
            <div className="text-sm">{errorMessage}</div>
          </div>
        ) : null}

        <div className="mt-6 md:mt-7">
          <GovernmentEmployerBasicRegistrationPreview basic={basic} onSubmit={handleSubmitBasic} />

          <hr className="border-gray-100 -mx-6 md:-mx-10 my-6" />

          <GovernmentEmployerLocationRegistrationPreview location={location} onSubmit={handleSubmitLocation} />

          <hr className="border-gray-100 -mx-6 md:-mx-10 my-6" />

          <GovernmentEmployerPicRegistrationPreview pic={pic} onSubmit={handleSubmitPic} />

          <hr className="border-gray-100 -mx-6 md:-mx-10 my-6" />

          <GovernmentEmployerDocumentRegistrationPreview document={document} onSubmit={handleSubmitDocument} />

          <hr className="border-gray-100 -mx-6 md:-mx-10 my-6" />

          <div className="space-y-3">
            {consents.map((consent) => {
              return (
                <div className="flex items-center gap-3" key={consent.id}>
                  <Checkbox
                    className="mt-1"
                    id={consent.id}
                    onCheckedChange={(checked) => handleUpdateConsent(consent.id, checked)}
                    checked={consent.checked}
                  />
                  <label htmlFor={consent.id} className="text-sm font-normal cursor-pointer">
                    {consent.text}
                  </label>
                </div>
              );
            })}
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <Button type="button" className="w-full text-sm font-bold" variant="outline" onClick={() => setIsShowPreview(false)}>
              Kembali
            </Button>
            <Button
              type="button"
              className="w-full text-sm font-bold"
              disabled={!consents.every((it) => it.checked) || isRegistering}
              onClick={handleRegister}
            >
              {isRegistering ? "Sedang mendaftar..." : "Daftar Sekarang"}
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <h1 className="text-xl font-bold">Daftar sebagai pemberi kerja</h1>
      <RegistrationCardInfo />
      <div className="mt-6 md:mt-7 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <h4 className="text-base font-bold flex-1">{steps[step]}</h4>
          <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
            <RadialProgress size={16} strokeWidth={3} progress={((step + 1) / steps.length) * 100} />
            Langkah {step + 1}/{steps.length}
          </div>
        </div>
        {getFormStep()}
      </div>
    </>
  );
}
